import React, { useEffect, useState, useMemo } from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FormControlLabel from '@mui/material/FormControlLabel';
import MUIRadioGroup from '@mui/material/RadioGroup';
import ModalWithInput from '@ui-parts/ModalWithInput';
import { useForm } from 'react-hook-form';
import NotificationImportantRoundedIcon from '@mui/icons-material/NotificationImportantRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';

import ProgressTracker from '@ui-parts/ProgressTracker';
import Button from '@ui-elements/Button';
import EditButton from '@ui-elements/EditButton';
import SaveButton from '@ui-elements/SaveButton';
import Radio from '@ui-elements/Radio';
import RequiredLabel from '@ui-elements/RequiredLabel';

import InputWithLabel from '@ui-parts/InputWithLabel';

import {
  convertGenderToDisplayNameArray,
  convertAgeToDisplayNameArray,
  convertAreaCdsToDisplayNameArray,
  convertSpecialPanelCheckedItemsToArray,
  extractDateFromIsoDate,
} from '@functional';
import {
  callThunk,
  fetchSavedDeliverySettings,
  fetchInvoiceRecipient,
  createInvoiceRecipient,
  applySurvey,
  createSurveyPayments,
  updateSurveyPayments,
  getAddressesByZipCodeInvoice,
  fetchAllowedPaymentType,
  getAuthenticatedUser,
} from '~/api';
import { PALETTE, FONT_WEIGHTS, FONT_SIZES } from '~/assets/styles/base';
import {
  APP_ORIGIN,
  LOCALE_STRING,
  PAYMENT_TYPE,
  SURVEY_STATUSES,
  PROGRESS_TRACKER_LABELS,
  PROGRESS_TYPE,
  SURVEY_PROGRESSES,
  SPECIAL_PANEL_CATEGORIES,
} from '~/constants';
import { ROUTES } from '~/constants/routes';
import { GMOASK_STATUS } from '~/constants/api';
import {
  emailValidationRule,
  personInChargeValidationRule,
  invoiceRecipientNameValidationRule,
  departmentValidationRule,
  zipCodeValidationRule,
  prefValidationRule,
  requiredAddressValidationRule,
} from '~/constants/validationRules';

import CautionaryModal from './CautionaryModal/index';

const Title = styled(Typography)`
  color: ${PALETTE.primary.main};
  font-size: 2.4rem;
  margin: 25px 0 16px;
`;

const Cap = styled(Typography)`
  line-height: 2.1;
  white-space: pre-line;
`;

const SurveyBox = styled(Box)`
  width: 800px;
  margin: 0 auto;
  border: 1px solid ${PALETTE.border.frame};
  background-color: ${PALETTE.white};
  margin-top: 28px;
  padding: 25px 36px 30px;
`;

const SurveyBoxTitle = styled(Typography)`
  font-weight: ${FONT_WEIGHTS.size2};
  margin-bottom: 18px;
`;

const ListItems = styled.div`
  margin-bottom: 7px;
`;

const ListTitle = styled.div`
  display: table-cell;
  vertical-align: middle;
  min-width: 183px;
  min-height: 32px;
  padding: 8px 0;
  background-color: ${PALETTE.blue.main};
  text-align: center;
  font-size: ${FONT_SIZES.body4};
`;

const ListText = styled.div`
  display: table-cell;
  vertical-align: middle;
  font-size: ${FONT_SIZES.body3};
  width: 100%;
  line-height: 1.7;
  padding: 4px 0 4px 22px;
  word-break: break-all;
`;

const ListTextInner = styled.div`
  display: flex;
  line-height: 1.4;
  padding-bottom: 0.7rem;
  margin-top: 1.1rem;
  align-items: center;
  border-bottom: 1px solid ${PALETTE.border.input};
`;

const ListTextInnerTitle = styled.div`
  word-break: keep-all;
  min-width: 7rem;
  width: 7rem;
  margin-right: 1.5rem;
  padding: 0.4rem;
  font-size: 1.1rem;
`;

const ConditionValue = styled.span`
  line-height: 2.5rem;
`;

const SurveyBoxFooter = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled(Button)`
  font-weight: normal;
  margin-top: 18px;
  width: 250px;
`;

const EstimatePriceTotalLabel = styled.span`
  margin-right: 1.5rem;
`;

const Sum = styled(Typography)``;

const Amount = styled(Typography)`
  font-size: 2.4rem;
  font-weight: ${FONT_WEIGHTS.size2};
`;

const Currency = styled(Typography)`
  font-weight: ${FONT_WEIGHTS.size2};
`;

const Payment = styled.div`
  margin-top: 90px;
  width: 800px;
  margin: 90px auto 60px;
`;

const PaymentTitle = styled(Typography)`
  font-size: 2.4rem;
  font-weight: ${FONT_WEIGHTS.size2};
  margin-bottom: 18px;
`;

const PaymentText = styled.div`
  line-height: 1.8;
  white-space: pre-line;
`;

const PaymentTextCaptions = styled.div`
  margin-top: 10px;
  margin-bottom: 34px;
  line-height: 1.7;
`;

const PaymentTextColored = styled.span`
  background: ${PALETTE.blue.note};
`;

const CreditBox = styled.div`
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.frame};
  margin-bottom: 24px;
  box-shadow: 2px 2px 4px #959fa815;
  min-height: 89px;
  border-radius: 8px;
  padding: 32px 33px;
`;

const InvoiceBox = styled.div`
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.frame};
  box-shadow: 2px 2px 4px #959fa815;
  border-radius: 8px;
  padding: 32px 33px;
`;

const CreditBoxTitle = styled.div`
  font-weight: ${FONT_WEIGHTS.size2};
  margin-right: 52px;
  word-break: keep-all;
  .MuiRadio-root {
    padding-bottom: 6px;
  }
`;

const CreditBoxText = styled.div`
  font-size: ${FONT_SIZES.body4};
  line-height: 1.6;
  white-space: pre-line;
`;

const InvoiceBoxTitle = styled.div`
  font-weight: ${FONT_WEIGHTS.size2};
  word-break: keep-all;
  margin-right: 135px;
  .MuiRadio-root {
    padding-bottom: 6px;
  }
`;

const InvoiceBoxText = styled.div`
  font-size: ${FONT_SIZES.body4};
  line-height: 1.8;
  white-space: pre-line;
`;

const InnerFlexBox = styled.div`
  display: flex;
  align-items: center;
`;
const NotificationBox = styled.div`
  display: flex;
  color: ${PALETTE.text.area};
  font-weight: bold;
  border: 2px solid ${PALETTE.border.frame};
  padding: 13px 9px 12px 14px;
  width: 230px;
  height: 80px;
  margin-top: 25px;
  align-items: center;
  margin-right: ${(props) => props.marginRight || '24px'};
`;
const NotificationIcon = styled(NotificationImportantRoundedIcon)`
  color: ${PALETTE.warning.main};
  font-size: 40px;
  margin-right: 7px;
`;

const BlockIcon = styled(BlockRoundedIcon)`
  color: ${PALETTE.warning.red};
  font-size: 40px;
  margin-right: 7px;
`;
const PaymentInfoTitle = styled(Typography)`
  font-weight: ${FONT_WEIGHTS.size2};
  margin-bottom: 6px;
`;

const SubmitButton = styled(Button)`
  display: block;
  width: 280px;
  height: 48px;
  margin: 30px 0 0 auto;
`;

const InputArea = styled.div`
  width: 502px;
  margin: 49px auto 0;
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 5px;
  .MuiFormControl-root {
    display: flex;
    width: 417px;
  }
  input.Mui-disabled {
    font-size: ${FONT_SIZES.bodyDefault};
    -webkit-text-fill-color: ${PALETTE.text.main};
  }
`;

const ButtonContainer = styled.div`
  position: absolute;
  right: 29px;
  top: 52px;
`;

const MultipleButtonContainer = styled.div`
  position: absolute;
  right: 29px;
  top: 49px;
`;

const MultipleInputWrapper = styled.div`
  position: relative;
  margin-top: 28px;
`;

const SmallInputWrapper = styled(InputWrapper)`
  .MuiFormControl-root {
    width: 160px;
  }
`;
const LongInputWrapper = styled(InputWrapper)`
  .MuiFormControl-root {
    width: 453px;
  }
`;

const MultipleInputInner = styled.div`
  border: 1px solid ${PALETTE.border.input};
  border-radius: 9px;
  padding: 3px 16px 45px;
  margin-top: 13px;
`;

const Label = styled.span`
  font-size: ${FONT_SIZES.bodyDefault};
  font-weight: ${FONT_WEIGHTS.size2};
  margin-left: 21px;
`;

const modalInputStyle = {
  marginLeft: '31px',
  position: 'left',
  width: '100%',
  errorZIndex: 10000,
};

const modalSmallInputStyle = {
  width: '132px',
  position: 'left',
  errorZIndex: 10000,
};

const Wrapper = styled.div`
  & [color='error'] span {
    word-break: keep-all;
  }
`;

// ヘッダーなどコンポーネント外にも適用する為、globalStyle
/* istanbul ignore next createGlobalStyleに対してテスト実施が困難なため */
const GlobalDisabledStyle = createGlobalStyle`
  ${({ $disabled }) =>
    $disabled &&
    css`
      body {
        pointer-events: none;
        opacity: 0.7;
      }
    `}
`;

const modalInitState = {
  email: false,
  personInCharge: false,
  name: false,
  department: false,
  address: false,
};

function SurveyApply() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { id: surveyId } = useParams();

  const { areas, survey, qualifications, surveyPayment, invoiceRecipient, user } = useSelector(
    (state) => state
  );

  const [openModal, setOpenModal] = useState(modalInitState);
  const [isBodyDisabled, setIsBodyDisabled] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [openCautionaryModal, setOpenCautionaryModal] = useState(false);

  const [qualificationArea, setQualificationArea] = useState([]);
  const [selectedSpecialPanelNames, setSelectedSpecialPanelNames] = useState({
    jobGroup: [],
    industry: [],
    jobType: [],
    numOfWorkers: [],
    jobPosition: [],
    householdIncome: [],
    marriageStatus: [],
    childrenStatus: [],
  });

  /**
   * =============
   * useForm(バリデーション)
   * =============
   */
  const {
    control: controlEmail,
    handleSubmit: handleSubmitEmail,
    setValue: setEmailValue,
    getValues: getValuesEmail,
    clearErrors: clearErrorsEmail,
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    shouldFocusError: false,
    reValidateMode: 'onSubmit',
  });

  const {
    control: controlPersonInCharge,
    handleSubmit: handleSubmitPersonInCharge,
    setValue: setPersonInChargeValue,
    getValues: getValuesPersonInCharge,
    clearErrors: clearErrorsPersonInCharge,
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    shouldFocusError: false,
    reValidateMode: 'onSubmit',
  });

  const {
    control: controlName,
    handleSubmit: handleSubmitName,
    setValue: setNameValue,
    getValues: getValuesName,
    clearErrors: clearErrorsName,
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    shouldFocusError: false,
    reValidateMode: 'onSubmit',
  });

  const {
    control: controlDepartment,
    handleSubmit: handleSubmitDepartment,
    setValue: setDepartmentValue,
    getValues: getValuesDepartment,
    clearErrors: clearErrorsDepartment,
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    shouldFocusError: false,
    reValidateMode: 'onSubmit',
  });

  const {
    control: controlAddresses,
    handleSubmit: handleSubmitAddresses,
    setValue: setAddressesValue,
    getValues: getValuesAddresses,
    clearErrors: clearErrorsAddresses,
  } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    shouldFocusError: false,
    reValidateMode: 'onSubmit',
  });

  /**
   * 申し込みボタンを押したときにまとめてバリデーションする用。
   * 各モーダルでのバリデーションもしているが、申し込み時に再度確認（readOnlyのインプットフォームの値も含め）。
   */
  const {
    handleSubmit: handleApplySubmit,
    register,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  /**
   * =============
   * functions
   * =============
   */
  const closeModal = ({ modalName }) => {
    // 保存せずに閉じた場合にinputの内容をリセットする
    switch (modalName) {
      case 'email':
        setEmailValue('email', invoiceRecipient.email);
        setValue('email', invoiceRecipient.email);
        // モーダルを閉じる
        setOpenModal(modalInitState);
        clearErrorsEmail();
        break;
      case 'personInCharge':
        setPersonInChargeValue('personInCharge', invoiceRecipient.personInCharge);
        setValue('personInCharge', invoiceRecipient.personInCharge);
        // モーダルを閉じる
        setOpenModal(modalInitState);
        clearErrorsPersonInCharge();
        break;
      case 'name':
        setNameValue('name', invoiceRecipient.name);
        setValue('name', invoiceRecipient.name);
        // モーダルを閉じる
        setOpenModal(modalInitState);
        clearErrorsName();
        break;
      case 'department':
        setDepartmentValue('department', invoiceRecipient.department);
        setValue('department', invoiceRecipient.department);
        // モーダルを閉じる
        setOpenModal(modalInitState);
        clearErrorsDepartment();
        break;
      case 'address':
        setAddressesValue('zip', invoiceRecipient.zip);
        setValue('zip', invoiceRecipient.zip);

        setAddressesValue('pref', invoiceRecipient.pref);
        setValue('pref', invoiceRecipient.pref);

        setAddressesValue('address', invoiceRecipient.address);
        setValue('address', invoiceRecipient.address);

        // モーダルを閉じる
        setOpenModal(modalInitState);
        clearErrorsAddresses();
        break;
      /* istanbul ignore next defaultの場合のテストが難しいため */
      default:
        break;
    }
  };

  const handleClickGoToSurvey = () => {
    navigate(ROUTES.USER.SURVEY.replace(':id', survey.id), {
      state: { referrer: ROUTES.USER.SURVEY_APPLY },
    });
  };

  const handleZipCodeSearch = async (zip) => {
    if (zip.match(zipCodeValidationRule.pattern.value) != null) {
      try {
        const {
          payload: { result: addresses },
        } = await dispatch(
          callThunk({
            action: getAddressesByZipCodeInvoice,
            payload: zip,
            skipLoading: true,
          })
        );
        const pref = addresses[0].prefChar;
        const address = addresses[0].cityChar + addresses[0].townChar;
        setAddressesValue('pref', pref);
        setAddressesValue('address', address);
      } catch (e) {
        /* istanbul ignore next 特にUTする意味ないのでカバレッジから外す */
        console.error(e);
      }
    }
  };

  // 申し込む
  const apply = async () => {
    // 請求書払い
    const callSurveyPaymentsAction = async () => {
      const res = await dispatch(
        callThunk({
          action: surveyPayment.id == null ? createSurveyPayments : updateSurveyPayments,
          payload: { surveyId: Number(surveyId), type: surveyPayment.type },
        })
      );
      return res;
    };
    // 請求書情報を作成する。初回請求書決済の時のみ。
    if (invoiceRecipient.id == null && surveyPayment.type === PAYMENT_TYPE.INVOICE) {
      await dispatch(
        callThunk({
          action: createInvoiceRecipient,
          payload: {
            name: getValuesName('name'),
            email: getValuesEmail('email'),
            personInCharge: getValuesPersonInCharge('personInCharge'),
            department: getValuesDepartment('department'),
            zip: getValuesAddresses('zip'),
            pref: getValuesAddresses('pref'),
            address: getValuesAddresses('address'),
          },
        })
      );
    }
    const surveyPaymentsRes = await callSurveyPaymentsAction();
    /* istanbul ignore else elseテストも実施しているが、カバレッジを通すには不要なelseを記述しないといけない為 */
    if (surveyPaymentsRes.payload.status === GMOASK_STATUS.OK) {
      const res = await dispatch(
        callThunk({
          action: applySurvey,
          payload: { surveyId: Number(surveyId) },
        })
      );
      // 成功した場合は、completedページへ
      /* istanbul ignore else elseテストも実施しているが、カバレッジを通すには不要なelseを記述しないといけない為 */
      if (res.payload.status === GMOASK_STATUS.OK) {
        // 初回と再申請でnavigate先を変更
        if (res.payload.result.previousSurveyStatusId === SURVEY_STATUSES.REJECTED) {
          navigate(ROUTES.USER.SURVEY_REAPPLY_COMPLETED.replace(':id', surveyId));
        } else {
          navigate(ROUTES.USER.SURVEY_APPLY_COMPLETED.replace(':id', surveyId));
        }
      }
    }
  };

  const handleApply = async () => {
    if (surveyPayment.type === PAYMENT_TYPE.CREDIT_CARD) {
      setOpenCautionaryModal(true);
    } else {
      handleApplySubmit(apply)();
    }
  };

  const getDate = extractDateFromIsoDate(survey.deliveryDate);

  /**
   * =============
   * スペパネ
   * =============
   */
  const selectedSpecialPanelJobGroup = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.jobGroup,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.jobGroup]);

  const selectedSpecialPanelIndustry = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.industry,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.industry]);

  const selectedSpecialPanelJobType = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.jobType,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.jobType]);

  const selectedSpecialPanelNumOfWorkers = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.numOfWorkers,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.numOfWorkers]);

  const selectedSpecialPanelJobPosition = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.jobPosition,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.jobPosition]);

  const selectedSpecialPanelHouseholdIncome = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.householdIncome,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.householdIncome]);

  const selectedSpecialPanelMarriageStatus = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.marriageStatus,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.marriageStatus]);

  const selectedSpecialPanelChildrenStatus = useMemo(() => {
    return convertSpecialPanelCheckedItemsToArray(
      qualifications.onSearched.specialConditions.checkedItems.childrenStatus,
      survey.specialPanelDisplaySettingId,
      true
    );
  }, [qualifications.onSearched.specialConditions.checkedItems.childrenStatus]);

  /**
   * =============
   * useEffect
   * =============
   */
  // 初期表示
  useEffect(() => {
    setIsBodyDisabled(false);

    /**
     * リロード有無で変化する処理。
     * 前画面から送信した値がないときは配信設定情報取り直す。
     */
    (async () => {
      if (location.state === null) {
        await dispatch(
          callThunk({
            action: fetchSavedDeliverySettings,
            payload: { surveyId },
          })
        );
      } else {
        // ユーザー登録直後は、storeの有無に応じてユーザー情報と請求書データ取得
        // user.nameと比較しているのはログイン直後にアンケート作成 -> 申込と遷移すると、 user.id （とcategoryのデフォ値）だけがセットされている状態があるので

        let userId = user.id;
        if (user.name === '') {
          const {
            payload: { result },
          } = await dispatch(
            callThunk({
              action: getAuthenticatedUser,
            })
          );
          userId = result.id;
        }
        if (invoiceRecipient.userId === null) {
          await dispatch(
            callThunk({
              action: fetchInvoiceRecipient,
              payload: { userId },
            })
          );
        }
      }

      /**
       * リロード有無関係なく必ず行う処理
       */
      // 請求書支払い可能かを取得
      await dispatch(
        callThunk({
          action: fetchAllowedPaymentType,
        })
      );
    })();
  }, []);

  useEffect(() => {
    // モーダル内React-formの初期値
    setEmailValue('email', invoiceRecipient.email);
    setPersonInChargeValue('personInCharge', invoiceRecipient.personInCharge);
    setNameValue('name', invoiceRecipient.name);
    setDepartmentValue('department', invoiceRecipient.department);
    setAddressesValue('zip', invoiceRecipient.zip);
    setAddressesValue('pref', invoiceRecipient.pref);
    setAddressesValue('address', invoiceRecipient.address);

    // readonlyのReact-form input nullが入るとエラーになるので空文字
    setValue('email', invoiceRecipient.email || '');
    setValue('personInCharge', invoiceRecipient.personInCharge || '');
    setValue('name', invoiceRecipient.name || '');
    setValue('department', invoiceRecipient.department || '');
    setValue('zip', invoiceRecipient.zip || '');
    setValue('pref', invoiceRecipient.pref || '');
    setValue('address', invoiceRecipient.address || '');
  }, [invoiceRecipient]);

  useEffect(() => {
    // 登録、下書き、却下以外は確定しているので編集不可
    /* istanbul ignore else includesされないテストも実施しているが、カバレッジを通すには不要なelseを記述しないといけない為 */
    if (
      [
        SURVEY_STATUSES.REGISTERING,
        SURVEY_STATUSES.UNDER_REVIEW,
        SURVEY_STATUSES.REJECTED,
      ].includes(survey.surveyStatusId)
    ) {
      setIsReadOnly(false);
    }
  }, [survey.surveyStatusId]);

  useEffect(() => {
    if (surveyPayment.redirectUrl) {
      const url = new URL(surveyPayment.redirectUrl);
      // 完了画面の時はnavigate
      /* istanbul ignore if APP_ORIGINはdotenv経由でcoverageオプション時に読ませるとcoverageFileが生成されないので除外 */
      if (`${url.protocol}//${url.host}` === APP_ORIGIN) {
        navigate(surveyPayment.redirectUrl);
      } else {
        window.location.href = surveyPayment.redirectUrl;
      }
    }
  }, [surveyPayment.redirectUrl]);

  // 地域
  useEffect(() => {
    setQualificationArea(
      convertAreaCdsToDisplayNameArray(areas.areas, qualifications.onSearched.areas.checkedAreaCds)
    );
  }, [qualifications.onSearched.areas.checkedAreaCds]);

  // スペパネ
  useEffect(() => {
    setSelectedSpecialPanelNames({
      jobGroup: selectedSpecialPanelJobGroup,
      industry: selectedSpecialPanelIndustry,
      jobType: selectedSpecialPanelJobType,
      numOfWorkers: selectedSpecialPanelNumOfWorkers,
      jobPosition: selectedSpecialPanelJobPosition,
      householdIncome: selectedSpecialPanelHouseholdIncome,
      marriageStatus: selectedSpecialPanelMarriageStatus,
      childrenStatus: selectedSpecialPanelChildrenStatus,
    });
  }, [
    selectedSpecialPanelJobGroup,
    selectedSpecialPanelIndustry,
    selectedSpecialPanelNumOfWorkers,
    selectedSpecialPanelJobPosition,
    selectedSpecialPanelHouseholdIncome,
    selectedSpecialPanelMarriageStatus,
    selectedSpecialPanelChildrenStatus,
  ]);

  /**
   * =============
   * For Modal
   * =============
   */
  const modalInputProps = [
    {
      name: 'email',
      modal: {
        titleProps: {
          title: t('invoiceRecipient.editModalTitle', { area: t('invoiceRecipient.email') }),
        },
        inputs: [
          {
            rules: emailValidationRule,
            nameProp: 'email',
            placeholder: t('invoiceRecipient.email'),
            dataTestId: 'invoiceRecipientEmailInput',
            ...modalInputStyle,
            handleChange: (e) => {
              setEmailValue('email', e.target.value);
            },
            handleBlur: (e) => {
              setEmailValue('email', e.target.value.trim());
            },
            inputProps: { maxLength: 100 },
          },
        ],
        control: controlEmail,
        handleClickSetting: handleSubmitEmail(() => {
          dispatch({
            type: 'invoiceRecipient/change',
            payload: {
              email: getValuesEmail('email'),
            },
          });
          // モーダルを閉じる
          setOpenModal(modalInitState);
        }),
        dataTestId: 'ModalInput-email',
      },
    },
    {
      name: 'personInCharge',
      modal: {
        titleProps: {
          title: t('invoiceRecipient.editModalTitle', {
            area: t('invoiceRecipient.personInCharge'),
          }),
        },
        inputs: [
          {
            rules: personInChargeValidationRule,
            nameProp: 'personInCharge',
            placeholder: t('invoiceRecipient.personInCharge'),
            dataTestId: 'invoiceRecipientPersonInChargeInput',
            ...modalInputStyle,
            handleChange: (e) => {
              setPersonInChargeValue('personInCharge', e.target.value);
            },
            handleBlur: (e) => {
              setPersonInChargeValue('personInCharge', e.target.value.trim());
            },
            inputProps: { maxLength: 50 },
          },
        ],
        control: controlPersonInCharge,
        handleClickSetting: handleSubmitPersonInCharge(() => {
          dispatch({
            type: 'invoiceRecipient/change',
            payload: {
              personInCharge: getValuesPersonInCharge('personInCharge'),
            },
          });
          // モーダルを閉じる
          setOpenModal(modalInitState);
        }),
        dataTestId: 'ModalInput-personInCharge',
      },
    },
    {
      name: 'name',
      modal: {
        titleProps: {
          title: t('invoiceRecipient.editModalTitle', {
            area: t('invoiceRecipient.invoiceRecipientName'),
          }),
        },
        inputs: [
          {
            rules: invoiceRecipientNameValidationRule,
            nameProp: 'name',
            placeholder: t('invoiceRecipient.invoiceRecipientName'),
            dataTestId: 'invoiceRecipientNameInput',
            ...modalInputStyle,
            handleChange: (e) => {
              setNameValue('name', e.target.value);
            },
            handleBlur: (e) => {
              setNameValue('name', e.target.value.trim());
            },
            inputProps: { maxLength: 50 },
          },
        ],
        control: controlName,
        handleClickSetting: handleSubmitName(() => {
          dispatch({
            type: 'invoiceRecipient/change',
            payload: {
              name: getValuesName('name'),
            },
          });
          // モーダルを閉じる
          setOpenModal(modalInitState);
        }),
        dataTestId: 'ModalInput-name',
      },
    },
    {
      name: 'department',
      modal: {
        titleProps: {
          title: t('invoiceRecipient.editModalTitle', {
            area: t('invoiceRecipient.department'),
          }),
        },
        inputs: [
          {
            rules: departmentValidationRule,
            nameProp: 'department',
            placeholder: t('invoiceRecipient.department'),
            dataTestId: 'invoiceRecipientDepartmentInput',
            ...modalInputStyle,
            handleChange: (e) => {
              setDepartmentValue('department', e.target.value);
            },
            handleBlur: (e) => {
              setDepartmentValue('department', e.target.value.trim());
            },
            inputProps: { maxLength: 100 },
          },
        ],
        control: controlDepartment,
        handleClickSetting: handleSubmitDepartment(() => {
          dispatch({
            type: 'invoiceRecipient/change',
            payload: {
              department: getValuesDepartment('department'),
            },
          });
          // モーダルを閉じる
          setOpenModal(modalInitState);
        }),
        dataTestId: 'ModalInput-department',
      },
    },
    {
      name: 'address', // NOTE モーダルオープンと連動させるためaddressesではなくaddress
      modal: {
        titleProps: {
          title: t('invoiceRecipient.editModalTitle', {
            area: t('invoiceRecipient.addresses'),
          }),
        },
        inputs: [
          {
            rules: zipCodeValidationRule,
            nameProp: 'zip',
            label: t('invoiceRecipient.zip'),
            placeholder: t('invoiceRecipient.zip'),
            dataTestId: 'invoiceRecipientZipInput',
            ...modalSmallInputStyle,
            handleChange: (e) => {
              setAddressesValue('zip', e.target.value);
              handleZipCodeSearch(e.target.value);
            },
            handleBlur: (e) => {
              setAddressesValue('zip', e.target.value.trim());
            },
            inputProps: { maxLength: 7 },
          },
          {
            rules: prefValidationRule,
            nameProp: 'pref',
            label: t('invoiceRecipient.pref'),
            placeholder: t('invoiceRecipient.pref'),
            dataTestId: 'invoiceRecipientPrefInput',
            ...modalSmallInputStyle,
            handleChange: (e) => {
              setAddressesValue('pref', e.target.value);
            },
            handleBlur: (e) => {
              setAddressesValue('pref', e.target.value.trim());
            },
            inputProps: { maxLength: 32 },
          },
          {
            rules: requiredAddressValidationRule,
            nameProp: 'address',
            label: t('invoiceRecipient.address'),
            placeholder: t('invoiceRecipient.address'),
            dataTestId: 'invoiceRecipientAddressInput',
            ...modalInputStyle,
            handleChange: (e) => {
              setAddressesValue('address', e.target.value);
            },
            handleBlur: (e) => {
              setAddressesValue('address', e.target.value.trim());
            },
            inputProps: { maxLength: 128 },
          },
        ],
        control: controlAddresses,
        handleClickSetting: handleSubmitAddresses(() => {
          dispatch({
            type: 'invoiceRecipient/change',
            payload: {
              zip: getValuesAddresses('zip'),
              pref: getValuesAddresses('pref'),
              address: getValuesAddresses('address'),
            },
          });
          // モーダルを閉じる
          setOpenModal(modalInitState);
        }),
        dataTestId: 'ModalInput-address',
      },
    },
  ];

  return (
    <Wrapper>
      <GlobalDisabledStyle $disabled={isBodyDisabled} />
      <ProgressTracker
        width="618px"
        labels={PROGRESS_TRACKER_LABELS[PROGRESS_TYPE.SURVEY].map((label) => t(label))}
        currentNum={SURVEY_PROGRESSES[ROUTES.USER.SURVEY_APPLY]}
      />

      <Title component="p" align="center" variant="variant">
        {t('invoiceRecipient.warningTitle')}
      </Title>
      <Cap component="p" align="center" variant="bodyDefault">
        {t('invoiceRecipient.warningMessage')}
      </Cap>
      <SurveyBox data-testid="Pages-surveyApply-SurveyBox">
        <SurveyBoxTitle component="p" variant="bodyDefault">
          {t('invoiceRecipient.applyDetails')}
        </SurveyBoxTitle>
        <ListItems>
          <ListTitle>{t('invoiceRecipient.survey.surveyTitle')}</ListTitle>
          <ListText>{survey.name}</ListText>
        </ListItems>
        <ListItems>
          <ListTitle>{t('invoiceRecipient.survey.conditions')}</ListTitle>
          <ListText>
            {convertGenderToDisplayNameArray(qualifications.onSearched.gender).length > 0 && (
              <ListTextInner>
                <ListTextInnerTitle data-testid="Pages-surveyApply-survey-gender-title">
                  {t('quotas.conditions.titles.gender')}
                </ListTextInnerTitle>
                <ConditionValue data-testid="Pages-surveyApply-survey-gender-value">
                  {convertGenderToDisplayNameArray(qualifications.onSearched.gender).join('、')}
                </ConditionValue>
              </ListTextInner>
            )}
            {convertAgeToDisplayNameArray(qualifications.onSearched.age).length > 0 && (
              <ListTextInner>
                <ListTextInnerTitle data-testid="Pages-surveyApply-survey-age-title">
                  {t('quotas.conditions.titles.age')}
                </ListTextInnerTitle>
                <ConditionValue data-testid="Pages-surveyApply-survey-age-value">
                  {convertAgeToDisplayNameArray(qualifications.onSearched.age).join('、')}
                </ConditionValue>
              </ListTextInner>
            )}
            {qualificationArea.length > 0 && (
              <ListTextInner>
                <ListTextInnerTitle data-testid="Pages-surveyApply-survey-area-title">
                  {t('quotas.conditions.titles.region')}
                </ListTextInnerTitle>
                <ConditionValue data-testid="Pages-surveyApply-survey-area-value">
                  {qualificationArea.join('、')}
                </ConditionValue>
              </ListTextInner>
            )}
            {survey?.specialPanelDisplaySettingId &&
              SPECIAL_PANEL_CATEGORIES[survey.specialPanelDisplaySettingId].map((category) => {
                if (selectedSpecialPanelNames[category].length > 0) {
                  return (
                    <ListTextInner key={category}>
                      <ListTextInnerTitle
                        data-testid={`Pages-surveyApply-survey-${category}-title`}
                        dangerouslySetInnerHTML={{
                          __html: `${t(`quotas.conditions.titles.${category}`)}`,
                        }}
                      />
                      <ConditionValue data-testid={`Pages-surveyApply-survey-${category}-value`}>
                        {selectedSpecialPanelNames[category].join('、')}
                      </ConditionValue>
                    </ListTextInner>
                  );
                }
                return null;
              })}
          </ListText>
        </ListItems>
        <ListItems>
          <ListTitle>{t('invoiceRecipient.survey.targetSamples')}</ListTitle>
          <ListText>
            {survey.targetSamplesTotal &&
              survey.targetSamplesTotal.toLocaleString(LOCALE_STRING.ja)}
          </ListText>
        </ListItems>
        <ListItems>
          <ListTitle>{t('invoiceRecipient.survey.numberOfQuestions')}</ListTitle>
          <ListText>
            {survey.numberOfQuestions && survey.numberOfQuestions.toLocaleString(LOCALE_STRING.ja)}
          </ListText>
        </ListItems>
        <ListItems>
          <ListTitle>{t('invoiceRecipient.survey.deliveryDate')}</ListTitle>
          <ListText>
            {t('invoiceRecipient.survey.deliveryDateFormat', {
              year: getDate.year,
              month: getDate.month,
              date: getDate.dt,
              hour: getDate.hour,
              minute: getDate.minute,
            })}
          </ListText>
        </ListItems>
        <SurveyBoxFooter>
          <StyledButton
            color="secondary"
            colorType="gray"
            size="small"
            onClick={handleClickGoToSurvey}
          >
            {t('invoiceRecipient.survey.rewind')}
          </StyledButton>
          <Sum variant="body3">
            <EstimatePriceTotalLabel>
              {t('invoiceRecipient.survey.estimatePriceTotal')}
            </EstimatePriceTotalLabel>
            <Amount component="span">
              {surveyPayment.estimatePriceTotal &&
                Number(surveyPayment.estimatePriceTotal).toLocaleString(LOCALE_STRING.ja)}
            </Amount>
            <Currency variant="bodyDefault" component="span">
              {t('invoiceRecipient.survey.yenWithTax')}
            </Currency>
          </Sum>
        </SurveyBoxFooter>
      </SurveyBox>
      <Payment>
        <PaymentTitle> {t('invoiceRecipient.paymentMethod')}</PaymentTitle>
        {user.allowInvoice && (
          <PaymentText variant="bodyDefault">
            {t('invoiceRecipient.paymentMethodNote')}
            <PaymentTextColored>{t('invoiceRecipient.paymentMethodNote2')}</PaymentTextColored>
            <span>{t('invoiceRecipient.paymentMethodNote3')}</span>
          </PaymentText>
        )}

        <PaymentTextCaptions>
          {user.allowInvoice && (
            <Typography variant="body4" component="div">
              {t('invoiceRecipient.paymentMethodCaption1')}
            </Typography>
          )}
          <Typography variant="body4" component="div">
            {t('invoiceRecipient.paymentMethodCaption2')}
          </Typography>
        </PaymentTextCaptions>

        <MUIRadioGroup
          name="type"
          value={surveyPayment.type}
          onChange={(e) => {
            dispatch({ type: 'surveyPayment/update', payload: { type: Number(e.target.value) } });
          }}
        >
          <CreditBox>
            <InnerFlexBox>
              <CreditBoxTitle>
                <FormControlLabel
                  data-testid="uiParts-RadioGroup-radio-0"
                  label={<Label>{t('invoiceRecipient.payOfCreditCard')}</Label>}
                  control={<Radio value={PAYMENT_TYPE.CREDIT_CARD} />}
                />
              </CreditBoxTitle>
              <div>
                <CreditBoxText>{t('invoiceRecipient.explainEpsilon')}</CreditBoxText>
              </div>
            </InnerFlexBox>
            <InnerFlexBox>
              <NotificationBox>
                <BlockIcon />
                <CreditBoxText>{t('invoiceRecipient.warningCreditCard1')}</CreditBoxText>
              </NotificationBox>
              <NotificationBox>
                <BlockIcon />
                <CreditBoxText>{t('invoiceRecipient.warningCreditCard2')}</CreditBoxText>
              </NotificationBox>
              <NotificationBox marginRight>
                <NotificationIcon />
                <CreditBoxText>{t('invoiceRecipient.warningCreditCard3')}</CreditBoxText>
              </NotificationBox>
            </InnerFlexBox>
          </CreditBox>

          {user.allowInvoice && (
            <InvoiceBox data-testid="pages-apply-InvoiceBox">
              <InnerFlexBox>
                <InvoiceBoxTitle>
                  <FormControlLabel
                    data-testid="uiParts-RadioGroup-radio-1"
                    label={<Label>{t('invoiceRecipient.payOfInvoice')}</Label>}
                    control={<Radio value={PAYMENT_TYPE.INVOICE} />}
                  />
                </InvoiceBoxTitle>
                <InvoiceBoxText>{t('invoiceRecipient.note')}</InvoiceBoxText>
              </InnerFlexBox>
              <InputArea>
                <PaymentInfoTitle variant="bodyDefault" component="p">
                  {t('invoiceRecipient.info')}
                </PaymentInfoTitle>
                <InputWrapper>
                  <InputWithLabel
                    required
                    label={t('invoiceRecipient.email')}
                    name="email"
                    dataTestId="invoiceRecipient-email"
                    value={invoiceRecipient.email}
                    disabled
                    handleChange={null}
                    inputProps={{
                      ...register('email', emailValidationRule),
                    }}
                    errorMessage={errors?.email?.message}
                    error={Boolean(errors?.email)}
                    width="100%"
                  />
                  <ButtonContainer>
                    {invoiceRecipient.id === null && !isReadOnly && (
                      <EditButton
                        name="email"
                        variant="outlined"
                        sx={{ marginLeft: 26 }}
                        handleClick={() => {
                          setOpenModal({ ...openModal, ...{ email: true } });
                        }}
                      />
                    )}
                  </ButtonContainer>
                </InputWrapper>
                <InputWrapper>
                  <InputWithLabel
                    required
                    label={t('invoiceRecipient.personInCharge')}
                    name="personInCharge"
                    dataTestId="invoiceRecipient-personInCharge"
                    value={invoiceRecipient.personInCharge}
                    disabled
                    handleChange={null}
                    inputProps={{
                      ...register('personInCharge', personInChargeValidationRule),
                    }}
                    errorMessage={errors?.personInCharge?.message}
                    error={Boolean(errors?.personInCharge)}
                    width="100%"
                  />
                  <ButtonContainer>
                    {invoiceRecipient.id === null && !isReadOnly && (
                      <EditButton
                        name="personInCharge"
                        variant="outlined"
                        sx={{ marginLeft: 26 }}
                        handleClick={() => setOpenModal({ ...openModal, personInCharge: true })}
                      />
                    )}
                  </ButtonContainer>
                </InputWrapper>
                <InputWrapper>
                  <InputWithLabel
                    required
                    label={t('invoiceRecipient.invoiceRecipientName')}
                    name="name"
                    dataTestId="invoiceRecipient-name"
                    value={invoiceRecipient.name}
                    disabled
                    handleChange={null}
                    inputProps={{
                      ...register('name', invoiceRecipientNameValidationRule),
                    }}
                    errorMessage={errors?.name?.message}
                    error={Boolean(errors?.name)}
                    width="100%"
                  />
                  <ButtonContainer>
                    {invoiceRecipient.id === null && !isReadOnly && (
                      <EditButton
                        name="invoiceRecipientName"
                        variant="outlined"
                        sx={{ marginLeft: 26 }}
                        handleClick={() => setOpenModal({ ...openModal, name: true })}
                      />
                    )}
                  </ButtonContainer>
                </InputWrapper>
                <InputWrapper>
                  <InputWithLabel
                    required
                    label={t('invoiceRecipient.department')}
                    name="department"
                    dataTestId="invoiceRecipient-department"
                    value={invoiceRecipient.department}
                    disabled
                    handleChange={null}
                    inputProps={{
                      ...register('department', departmentValidationRule),
                    }}
                    errorMessage={errors?.department?.message}
                    error={Boolean(errors?.department)}
                    width="100%"
                  />
                  <ButtonContainer>
                    {invoiceRecipient.id === null && !isReadOnly && (
                      <EditButton
                        name="department"
                        variant="outlined"
                        sx={{ marginLeft: 26 }}
                        handleClick={() => setOpenModal({ ...openModal, department: true })}
                      />
                    )}
                  </ButtonContainer>
                </InputWrapper>

                <MultipleInputWrapper>
                  <Typography variant="body3">
                    <RequiredLabel>{t('validation.requiredLabel')}</RequiredLabel>
                    {t('invoiceRecipient.addresses')}
                  </Typography>
                  <MultipleInputInner>
                    <SmallInputWrapper>
                      <InputWithLabel
                        label={t('invoiceRecipient.zip')}
                        name="zip"
                        dataTestId="invoiceRecipient-zip"
                        value={invoiceRecipient.zip}
                        disabled
                        handleChange={null}
                        inputProps={{
                          ...register('zip', zipCodeValidationRule),
                        }}
                        errorMessage={errors?.zip?.message}
                        error={Boolean(errors?.zip)}
                        width="100%"
                      />
                    </SmallInputWrapper>
                    <SmallInputWrapper>
                      <InputWithLabel
                        label={t('invoiceRecipient.pref')}
                        name="pref"
                        dataTestId="invoiceRecipient-pref"
                        value={invoiceRecipient.pref}
                        disabled
                        handleChange={null}
                        inputProps={{
                          ...register('pref', prefValidationRule),
                        }}
                        errorMessage={errors?.pref?.message}
                        error={Boolean(errors?.pref)}
                        width="100%"
                      />
                    </SmallInputWrapper>
                    <LongInputWrapper>
                      <InputWithLabel
                        label={t('invoiceRecipient.address')}
                        name="address"
                        dataTestId="invoiceRecipient-address"
                        value={invoiceRecipient.address}
                        disabled
                        handleChange={null}
                        inputProps={{
                          ...register('address', requiredAddressValidationRule),
                        }}
                        errorMessage={errors?.address?.message}
                        error={Boolean(errors?.address)}
                        width="100%"
                      />
                    </LongInputWrapper>
                    <MultipleButtonContainer>
                      {invoiceRecipient.id === null && !isReadOnly && (
                        <EditButton
                          name="addresses"
                          variant="outlined"
                          sx={{ marginLeft: 26 }}
                          handleClick={() => setOpenModal({ ...openModal, address: true })}
                        />
                      )}
                    </MultipleButtonContainer>
                  </MultipleInputInner>
                </MultipleInputWrapper>
              </InputArea>
            </InvoiceBox>
          )}
        </MUIRadioGroup>

        <SubmitButton
          color="secondary"
          variant="contained"
          colorType="yellow"
          disabled={isReadOnly}
          onClick={() => handleApply()}
          data-testid="uiElements-Button-handleApply"
        >
          {t('invoiceRecipient.apply')}
        </SubmitButton>
      </Payment>
      {modalInputProps.map((inputProp) => {
        return (
          <ModalWithInput
            key={inputProp.name}
            open={openModal[inputProp.name]}
            handleClose={() => closeModal({ modalName: inputProp.name })}
            inputs={inputProp.modal.inputs}
            control={inputProp.modal.control}
            titleProps={inputProp.modal.titleProps}
            width="601px"
            buttons={[
              <SaveButton
                key={inputProp.name}
                handleClick={(e) => {
                  inputProp.modal.handleClickSetting(e);
                }}
                label={t('appCommon.setting')}
              />,
            ]}
          />
        );
      })}
      <CautionaryModal
        open={openCautionaryModal}
        handleClose={() => setOpenCautionaryModal(false)}
        handleBody={() => setIsBodyDisabled(true)}
        surveyId={surveyId}
      />
    </Wrapper>
  );
}

export default SurveyApply;
